import { useState } from 'react';

import { CompletionRange, LocationFragment } from '@occc/graphql';
import { useAdvancedQueryParam } from '@occc/utils-query-params';

import { TransactionsFilterFormProps } from '../transactions-filter-form.component';
import {
  FraudCheckStatusFilterType,
  TransactionStatusFilterType,
} from '../transactions-filter-form.types';
import {
  endDateQueryParams,
  startDateQueryParams,
  defaultTransactionCompletedAtQueryParams,
  MAX_AMOUNT_PARAM,
  MIN_AMOUNT_PARAM,
  PHONE_NUMBER_PARAM,
  transactionStatusQueryParams,
  fraudCheckStatusQueryParams,
} from '../transactions-filter-form.constants';

export const useTransactionsFilter = () => {
  const [locationsFilter, setLocationsFilter] = useState<LocationFragment[] | undefined>(undefined);

  const [startDateFilter, setStartDateFilter] = useAdvancedQueryParam<Date | null | undefined>(
    startDateQueryParams,
  );

  const [endDateFilter, setEndDateFilter] = useAdvancedQueryParam<Date | null | undefined>(
    endDateQueryParams,
  );

  const [transactionStatusFilter, setTransactionStatusFilter] =
    useAdvancedQueryParam<TransactionStatusFilterType>(transactionStatusQueryParams);

  const [fraudCheckStatusFilter, setFraudCheckStatusFilter] =
    useAdvancedQueryParam<FraudCheckStatusFilterType>(fraudCheckStatusQueryParams);

  const [amountMinFilter, setAmountMinFilter] = useAdvancedQueryParam<number | undefined>({
    paramName: MIN_AMOUNT_PARAM,
    parse: (value) => parseFloat(value),
    defaultValue: undefined,
  });

  const [amountMaxFilter, setAmountMaxFilter] = useAdvancedQueryParam<number | undefined>({
    paramName: MAX_AMOUNT_PARAM,
    parse: (value) => parseFloat(value),
    defaultValue: undefined,
  });

  const [phoneNumberFilter, setPhoneNumberFilter] = useAdvancedQueryParam<string | undefined>({
    paramName: PHONE_NUMBER_PARAM,
    defaultValue: undefined,
  });

  const [transactionCompletedAtFilter, setTransactionCompletedAtFilter] = useAdvancedQueryParam<
    CompletionRange | undefined
  >(defaultTransactionCompletedAtQueryParams);

  const onReset = () => {
    setLocationsFilter(undefined);
    setTransactionCompletedAtFilter(undefined);
    setStartDateFilter(undefined);
    setEndDateFilter(undefined);

    setTransactionStatusFilter(undefined);
    setFraudCheckStatusFilter(undefined);

    setAmountMinFilter(undefined);
    setAmountMaxFilter(undefined);

    setPhoneNumberFilter(undefined);
  };

  const onSubmit: TransactionsFilterFormProps['onSubmit'] = ({
    locations,
    startDate,
    endDate,
    transactionCompletedAt,
    transactionStatus,
    fraudCheckStatus,
    amountMax,
    amountMin,
    phoneNumber,
  }) => {
    setLocationsFilter(locations);
    setTransactionCompletedAtFilter(transactionCompletedAt);
    setStartDateFilter(startDate);
    setEndDateFilter(endDate);

    setTransactionStatusFilter(transactionStatus);
    setFraudCheckStatusFilter(fraudCheckStatus);

    setAmountMinFilter(amountMin);
    setAmountMaxFilter(amountMax);

    setPhoneNumberFilter(phoneNumber);
  };

  return {
    locationsFilter,
    startDateFilter,
    endDateFilter,
    transactionStatusFilter,
    fraudCheckStatusFilter,
    amountMinFilter,
    amountMaxFilter,
    phoneNumberFilter,
    transactionCompletedAtFilter,

    onReset,
    onSubmit,
  };
};
